import { Box, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { Loader2 } from 'lucide-react';
import type { ProductGroupLevelCounts } from '@/lib/types';
import {
  MAX_CROSS_AXES,
  MAX_PRODUCT_GROUP_LEVEL,
  type PotentialAnalysisCrossAxisSetting,
  type PotentialAnalysisProductGroupTypeSetting,
} from '@/lib/types/feature/potential-analysis-setting';
import {
  crossCombinations,
  estimateRows,
  groupCountOf,
  ESTIMATED_ROWS_WARN_THRESHOLD,
  type CrossAxisDraft,
} from './estimate';
import { EstimateNote } from './EstimateNote';

/** 軸セレクトの「選ばない」を表す値 */
const NO_AXIS = '__none__';

interface CrossAxisSectionProps {
  /** 選べる商品グループタイプ（マスタに在るもの） */
  types: PotentialAnalysisProductGroupTypeSetting[];
  /** 軸1〜3の指定。長さは常に MAX_CROSS_AXES */
  axes: CrossAxisDraft[];
  onChange: (index: number, next: CrossAxisDraft) => void;
  /**
   * 軸に残っているがマスタに無いタイプ。
   *
   * 選択肢に出さないと、その軸が空欄のまま外せなくなり、保存で古いIDが送られ続ける。
   */
  missingAxes: PotentialAnalysisCrossAxisSetting[];
  levelCounts: ProductGroupLevelCounts[] | null;
  levelCountsLoading: boolean;
  /** 行数見積もり用の店舗数。取れなければ null */
  sellerCount: number | null;
}

/**
 * 掛け合わせ出力の設定（FKV3-13122）
 *
 * 「週 × 店舗 × 商品グループ1 × 商品グループ2 × 商品グループ3」を1行とする横持ちの出力。
 * 行数が各軸のグループ数の積で増えるため、既定の階層は1階層にしてある。
 *
 * 1アカウントに1組だけ持つ。複数の組み合わせを出し分けたい場合は都度実行の抽出を使う。
 */
export function CrossAxisSection({
  types,
  axes,
  onChange,
  missingAxes,
  levelCounts,
  levelCountsLoading,
  sellerCount,
}: CrossAxisSectionProps) {
  const combinations = crossCombinations(axes, levelCounts);
  const rows =
    combinations === null || combinations === 0 || sellerCount === null
      ? null
      : estimateRows(combinations, sellerCount);

  /** 他の軸で既に選ばれているタイプは選択肢から外す（同じタイプの掛け合わせは成立しない） */
  const selectableTypes = (index: number) => {
    const takenByOthers = new Set(
      axes
        .filter((_, i) => i !== index)
        .map((a) => a.productGroupTypeId)
        .filter((id): id is string => id !== null),
    );
    const selectable: { id: string; label: string }[] = types
      .filter((t) => !t.missing_from_master && !takenByOthers.has(t.product_group_type_id))
      .map((t) => ({ id: t.product_group_type_id, label: t.name ?? t.product_group_type_id }));

    // この軸に入っているのがマスタに無いタイプなら、選択肢に残して外せるようにする
    const current = axes[index].productGroupTypeId;
    const missing = missingAxes.find((a) => a.product_group_type_id === current);
    if (missing) {
      selectable.unshift({
        id: missing.product_group_type_id,
        label: `${missing.name ?? missing.product_group_type_id}（マスタに存在しません）`,
      });
    }
    return selectable;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        掛け合わせ出力（任意）
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        選んだ軸を列に並べた横持ちを追加で出力します。行数が各軸のグループ数の掛け算で増えるため、
        集計階層の既定は1階層です。軸を選ばなければ掛け合わせは出力されません。
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        {axes.map((axis, index) => {
          const counts = levelCounts?.find((c) => c.type_id === axis.productGroupTypeId);
          const groups = axis.productGroupTypeId
            ? groupCountOf(counts, axis.productGroupLevel)
            : null;
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mb: index < axes.length - 1 ? 1.5 : 0,
              }}
            >
              <Typography variant="body2" sx={{ width: 40, flexShrink: 0 }}>
                軸{index + 1}
              </Typography>
              <TextField
                select
                size="small"
                value={axis.productGroupTypeId ?? NO_AXIS}
                onChange={(e) =>
                  onChange(index, {
                    productGroupTypeId: e.target.value === NO_AXIS ? null : e.target.value,
                    // 軸を選んだ時点の階層は親が既定値を入れるため、ここでは引き継ぐ
                    productGroupLevel: axis.productGroupLevel,
                  })
                }
                inputProps={{ 'aria-label': `掛け合わせの軸${index + 1}` }}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <MenuItem value={NO_AXIS}>（選択しない）</MenuItem>
                {selectableTypes(index).map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                disabled={axis.productGroupTypeId === null}
                value={axis.productGroupLevel}
                onChange={(e) =>
                  onChange(index, {
                    productGroupTypeId: axis.productGroupTypeId,
                    productGroupLevel: Number(e.target.value),
                  })
                }
                inputProps={{ 'aria-label': `掛け合わせの軸${index + 1}の集計階層` }}
                sx={{ width: 160, flexShrink: 0 }}
              >
                {Array.from({ length: MAX_PRODUCT_GROUP_LEVEL }, (_, i) => i + 1).map((level) => (
                  <MenuItem key={level} value={level}>
                    {level === MAX_PRODUCT_GROUP_LEVEL ? `${level}階層（最下層）` : `${level}階層`}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ width: 120, flexShrink: 0, textAlign: 'right' }}>
                {axis.productGroupTypeId === null ? (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                ) : levelCountsLoading ? (
                  <Loader2 className="ml-auto h-3 w-3 animate-spin" />
                ) : groups === null ? (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                ) : (
                  <Typography variant="body2">{groups.toLocaleString()} グループ</Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Paper>

      {/* 行数が積で膨らむため、縦持ち以上に実行前の見積もりが要る */}
      {combinations !== null && combinations > 0 && (
        <Box sx={{ mt: 1 }}>
          <EstimateNote
            warning={rows !== null && rows >= ESTIMATED_ROWS_WARN_THRESHOLD}
            headline={
              `掛け合わせは ${combinations.toLocaleString()} 通り` +
              (rows !== null ? ` → 推定 約${rows.toLocaleString()} 行／2年` : '')
            }
            warningDetail="行数は各軸のグループ数の掛け算で増えます。いずれかの軸の集計階層を浅くすると大きく減ります。"
          />
        </Box>
      )}

      {axes.length > MAX_CROSS_AXES && (
        <Typography variant="caption" className="text-amber-600">
          軸は最大{MAX_CROSS_AXES}つです
        </Typography>
      )}
    </Box>
  );
}
